import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import toast        from 'react-hot-toast';
import Card         from '../../components/common/Card';
import { getCommandCenter } from '../../services/adminService';
// Generate fees via the SAME FeePayment endpoint the Payments page uses (was the legacy
// /api/payments/generate-monthly, which wrote to the dead Payment model).
import { generateMonthlyFees } from '../../services/feeService';
import { ktmThisMonth, formatBsMonth, adHint, formatDateDual } from '../../utils/calendar';
import DualDate from '../../components/common/DualDate';
import { getActivityLog } from '../../services/activityService';
import useAuth      from '../../hooks/useAuth';
import useImpersonation from '../../hooks/useImpersonation';
import { useCalendar } from '../../context/CalendarContext';
import { activityLabel, isSystemActivity } from '../../utils/activityLabels';
import { useConfirm } from '../../components/common/ConfirmModal';
import useTheme from '../../context/ThemeContext';
import useClubVocab from '../../hooks/useClubVocab';
import { useTenant } from '../../context/TenantContext';
import { hasClasses } from '../../utils/publicProfile';
import { TRIAL_ENABLED, APPROVALS_ENABLED } from '../../config/features';
import {
  MdPersonAdd, MdCampaign, MdEmojiEvents, MdPayments, MdReceipt,
  MdSchedule, MdWarning, MdCheckCircle, MdArrowForward, MdPeople,
  MdTrendingUp, MdTrendingDown, MdToday, MdHourglassTop, MdAutorenew,
  MdCardMembership,
  MdSpeed, MdExpandMore, MdExpandLess, MdBugReport, MdHistory,
  MdLanguage,
} from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';

// ── Helpers ────────────────────────────────────────────────────────────────
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
};

const fmt = (n) => Number(n || 0).toLocaleString();

const currentMonth = () => ktmThisMonth(); // Kathmandu 'YYYY-MM' (was browser-local)

// BS-primary month label with a muted AD hint, e.g. "2083 Asar · Jun" — for the fee-generate
// confirm/toasts, so a BS user never sees a bare "June 2026" mid-flow.
const currentMonthLabel = () => { const cm = ktmThisMonth(); return `${formatBsMonth(cm)} · ${adHint(cm)}`; };

// ── Section heading ────────────────────────────────────────────────────────
function SectionHead({ title, action, actionTo }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-[11px] font-medium text-subtle uppercase tracking-[0.12em]">{title}</h2>
      {action && actionTo && (
        <Link to={actionTo} className="text-muted hover:text-secondary text-xs font-medium flex items-center gap-0.5 transition-colors">
          {action} <MdArrowForward size={13} />
        </Link>
      )}
    </div>
  );
}

// ── Quick action button — supports both Link and button ───────────────────
function QA({ to, onClick, icon: Icon, label, primary, loading: isLoading }) {
  const { isReadOnly } = useImpersonation();
  const currentTheme = useTheme()?.currentTheme;
  const cls = `flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap disabled:cursor-not-allowed ${
    primary
      ? `${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 disabled:opacity-60`
      : 'bg-card text-secondary border border-divider hover:bg-secondary hover:text-card hover:border-secondary disabled:opacity-60'
  }`;
  if (onClick) {
    return (
      <button
        onClick={onClick}
        disabled={isLoading || isReadOnly}
        title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
        className={cls}>
        <Icon className={`text-base flex-shrink-0 ${isLoading ? 'animate-spin' : ''}`} />
        {isLoading ? 'Working…' : label}
      </button>
    );
  }
  return (
    <Link to={to} className={cls}>
      <Icon className="text-base flex-shrink-0" />{label}
    </Link>
  );
}

// ── Action item row ────────────────────────────────────────────────────────
function ActionItem({ icon: Icon, iconBg, iconColor, title, subtitle, to, linkLabel, extra }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5">
      <div className={`w-9 h-9 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon className={`${iconColor} text-lg`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-secondary leading-tight">{title}</p>
        <p className="text-xs text-muted mt-0.5">{subtitle}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {extra}
        <Link
          to={to}
          className="text-xs font-medium text-card bg-secondary hover:opacity-90 flex items-center gap-0.5 px-2.5 py-1 rounded-full transition-all duration-200"
        >
          {linkLabel} <MdArrowForward size={13} />
        </Link>
      </div>
    </div>
  );
}

// ── Finance metric card ────────────────────────────────────────────────────
// Clickable membership stat card (gym/yoga/general). Real alpha tints so it reads in both
// themes; navigates to the Members list with the matching ?membership= filter pre-applied.
const MEMBERSHIP_TONES = {
  green:   { iconBg: 'bg-emerald-500/15', icon: 'text-emerald-500' },
  amber:   { iconBg: 'bg-amber-500/15',   icon: 'text-amber-500' },
  red:     { iconBg: 'bg-red-500/15',     icon: 'text-red-500' },
  neutral: { iconBg: 'bg-nav-hover',      icon: 'text-muted' },
};
function MembershipStatCard({ label, value, tone, icon: Icon, onClick }) {
  const t = MEMBERSHIP_TONES[tone] || MEMBERSHIP_TONES.neutral;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-card border border-divider rounded-2xl p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 active:scale-[0.99]"
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 ${t.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
          <Icon className={`${t.icon} text-xl`} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-muted font-medium uppercase tracking-wide">{label}</p>
          <p className="font-bold text-lg leading-tight mt-0.5 text-secondary">{value ?? 0}</p>
        </div>
      </div>
    </button>
  );
}

function FinCard({ label, value, sub, color, iconBg, icon: Icon }) {
  return (
    <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
          <Icon className={`${color} text-xl`} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-muted font-medium uppercase tracking-wide">{label}</p>
          <p className="font-bold text-base leading-tight mt-0.5 text-secondary">{value}</p>
          {sub && <p className="text-[11px] text-muted mt-0.5 leading-tight">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

// ── Today session row ──────────────────────────────────────────────────────
function SessionRow({ session }) {
  const currentTheme = useTheme()?.currentTheme;
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
        session.attendanceTaken ? 'bg-green-400' : 'bg-amber-400 animate-pulse'
      }`} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-secondary text-sm leading-tight truncate">{session.className}</p>
        <p className="text-xs text-muted mt-0.5">
          {session.startTime ? fmtTime(session.startTime) : 'No time set'}
          {session.trainerName ? ` · ${session.trainerName}` : ''}
        </p>
      </div>
      {session.attendanceTaken ? (
        <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-semibold flex-shrink-0">
          ✓ Done
        </span>
      ) : (
        <Link
          to="/admin/attendance"
          className={`text-xs ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} px-3 py-1.5 rounded-full font-medium hover:opacity-90 transition-all duration-200 flex-shrink-0`}
        >
          Take Attendance
        </Link>
      )}
    </div>
  );
}

// ── Start Setup card — shown on brand-new clubs ────────────────────────────
function StartSetupCard({ onSkip }) {
  const handleScroll = () => {
    const el = document.getElementById('setup-checklist');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <div className="bg-primary/10 border border-primary/20 rounded-2xl p-5 flex items-start gap-4">
      <div className="w-10 h-10 bg-primary/15 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
        <MdSpeed className="text-primary text-xl" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-secondary text-base leading-tight">Let's set up your club</p>
        <p className="text-muted text-xs mt-1 leading-snug">
          A few quick steps and you'll be ready to manage members, fees, and attendance.
        </p>
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          <button
            onClick={handleScroll}
            className="px-4 py-2 bg-primary text-white font-bold text-sm rounded-xl hover:bg-primary-dark transition-colors active:opacity-80 min-h-[40px]"
          >
            Start Setup
          </button>
          <button
            onClick={onSkip}
            className="text-muted text-sm font-medium hover:text-secondary transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}

// Re-word the backend checklist copy for the club's vocabulary (the strings come from the
// server, so we adapt them client-side). Taekwondo-family clubs are byte-identical: their vocab
// words already equal the originals (Student/Students, Trainer) and parent wording is kept.
// Gym/yoga/general swap student→member, parents→member (they have no "parents"), trainer→instructor
// for yoga. "class"/"classes" and "fee" wording are left untouched (classes apply to gyms too).
function vocabize(text, v) {
  if (!text) return text;
  const studentVocab = v.member === 'Student'; // taekwondo family → keep student/parent wording
  const swap = (str, from, to) => str
    .replace(new RegExp(`\\b${from}\\b`, 'g'), to)
    .replace(new RegExp(`\\b${from.toLowerCase()}\\b`, 'g'), to.toLowerCase());
  let out = swap(swap(text, 'Students', v.members), 'Student', v.member);
  if (!studentVocab) {
    out = swap(swap(out, 'Parents', v.members), 'Parent', v.member);
  }
  out = swap(swap(out, 'Trainers', v.trainers), 'Trainer', v.trainer);
  return out;
}

// ── Launch Readiness Checklist ─────────────────────────────────────────────
function SetupChecklist({ sc }) {
  const v = useClubVocab();
  const [collapsed, setCollapsed] = useState(() =>
    localStorage.getItem('ta_checklist_v1') === '1'
  );

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('ta_checklist_v1', next ? '1' : '0');
  };

  const { items, total, completed } = sc;
  const pct     = Math.round((completed / total) * 100);
  const allDone = items.filter(s => !s.optional).every(s => s.done);
  const nextStep = items.find(s => !s.done && !s.optional);

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-secondary text-sm">Launch Readiness</h3>
          <p className="text-xs text-muted mt-0.5">
            {allDone ? 'All key steps complete!' : `${completed} of ${total} steps complete`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-20 h-1.5 bg-nav-hover rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-bold text-primary">{pct}%</span>
          </div>
          <button
            onClick={toggle}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-nav-hover text-muted transition-colors"
            title={collapsed ? 'Expand checklist' : 'Collapse checklist'}
          >
            {collapsed ? <MdExpandMore className="text-lg" /> : <MdExpandLess className="text-lg" />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="mt-4">
          {allDone ? (
            <div className="flex items-center gap-3 bg-green-50 rounded-xl p-4">
              <MdCheckCircle className="text-green-500 text-2xl flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm text-green-800">Your club is ready to operate on TrainAxis!</p>
                <p className="text-xs text-green-600 mt-0.5">All key setup steps are complete. Keep growing!</p>
              </div>
            </div>
          ) : (
            <>
              {/* Recommended next step */}
              {nextStep && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
                  <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1.5">Up next</p>
                  <p className="text-sm font-bold text-amber-900 leading-tight">{vocabize(nextStep.label, v)}</p>
                  <p className="text-xs text-amber-700 mt-1.5 leading-snug">{vocabize(nextStep.hint, v)}</p>
                  <Link
                    to={nextStep.link}
                    className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold text-white bg-amber-500 hover:bg-amber-600 active:bg-amber-700 px-4 py-2.5 rounded-xl transition-colors w-full sm:w-auto justify-center sm:justify-start"
                  >
                    Do this now <MdArrowForward size={14} />
                  </Link>
                </div>
              )}

              {/* Checklist items */}
              <div className="space-y-0.5">
                {items.map(item => (
                  <div
                    key={item.id}
                    className={`flex items-start gap-3 px-3 py-2.5 rounded-xl transition-colors ${!item.done ? 'hover:bg-nav-hover' : ''}`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      item.done
                        ? 'bg-green-100'
                        : item.optional
                          ? 'border border-dashed border-gray-300'
                          : 'border-2 border-divider'
                    }`}>
                      {item.done && <MdCheckCircle className="text-green-500 text-[13px]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-tight ${item.done ? 'line-through text-muted' : 'font-medium text-secondary'}`}>
                        {vocabize(item.label, v)}
                        {item.optional && (
                          <span className="ml-2 text-[10px] bg-gray-100 text-subtle px-1.5 py-0.5 rounded font-normal">optional</span>
                        )}
                      </p>
                      {!item.done && <p className="text-xs text-muted mt-0.5 leading-snug">{vocabize(item.hint, v)}</p>}
                    </div>
                    {!item.done && (
                      <Link
                        to={item.link}
                        className="flex-shrink-0 text-xs font-semibold text-primary bg-primary-light hover:bg-secondary hover:text-card px-2.5 py-1 rounded-lg flex items-center gap-0.5 transition-colors"
                      >
                        Go <MdArrowForward size={11} />
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Launch Testing shortcut — always visible at the bottom of the card */}
      <div className="mt-3 pt-2 border-t border-divider flex items-center justify-end">
        <Link
          to="/admin/launch-testing"
          className="flex items-center gap-1.5 text-xs text-muted hover:text-primary transition-colors"
        >
          <MdBugReport className="text-sm" />
          Open Launch Testing
        </Link>
      </div>
    </Card>
  );
}

// ── Recent Activity widget ─────────────────────────────────────────────────
const ACTION_STYLE = {
  payment_recorded: 'text-green-600', payment_confirmed: 'text-green-600',
  payment_waived: 'text-muted',    proof_approved: 'text-green-600',
  proof_rejected: 'text-red-500',     reminder_sent: 'text-blue-500',
  create_expense: 'text-amber-600',   approve_expense: 'text-green-600',
  reject_expense: 'text-red-500',     student_archived: 'text-orange-500',
  student_restored: 'text-teal-600',  student_deleted: 'text-red-600',
  bulk_reminder_sent: 'text-blue-500', auto_reminder_run: 'text-blue-500',
};

function relTime(ts) {
  const diff  = Date.now() - new Date(ts).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return formatDateDual(ts).primary; // BS date (11px muted line is space-tight → BS-only)
}

function RecentActivity() {
  const { data, isPending } = useQuery({
    queryKey: ['activity-recent'],
    queryFn:  () => getActivityLog({ limit: 5, page: 1 }).then(r => r.data),
    staleTime: 1000 * 60 * 2,
  });

  const logs = data?.logs || [];

  return (
    <Card className="!p-0 overflow-hidden transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
      {isPending ? (
        <div className="space-y-2 p-3">
          {[1,2,3].map(i => <div key={i} className="h-10 bg-nav-hover animate-pulse rounded-xl" />)}
        </div>
      ) : logs.length === 0 ? (
        <div className="flex items-center gap-3 px-5 py-5">
          <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <MdHistory className="text-subtle text-lg" />
          </div>
          <div>
            <p className="text-sm font-semibold text-secondary">No activity yet</p>
            <p className="text-xs text-muted mt-0.5">Key actions will appear here once you start using the platform.</p>
          </div>
        </div>
      ) : (
        <div className="divide-y divide-divider">
          {logs.map(log => {
            const system = isSystemActivity(log);
            return (
              <div key={log._id} className="flex items-center gap-3 px-4 py-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  system ? 'bg-nav-hover text-muted' : 'bg-primary-light text-primary text-[11px] font-bold'
                }`}>
                  {system ? <MdAutorenew className="text-sm" /> : (log.actorName || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-secondary leading-tight truncate">
                    {!system && <span className="font-semibold">{log.actorName}</span>}
                    {!system && ' · '}
                    <span className={`font-medium ${ACTION_STYLE[log.action] || 'text-secondary'}`}>
                      {activityLabel(log)}
                    </span>
                    {log.targetName && <span className="text-muted"> — {log.targetName}</span>}
                  </p>
                </div>
                <span className="text-[11px] text-muted flex-shrink-0">{relTime(log.timestamp)}</span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── Mobile quick action grid ───────────────────────────────────────────────
const MOBILE_ACTIONS = [
  { to: '/admin/attendance',            Icon: MdCheckCircle, label: 'Attendance',      bg: 'bg-green-50',   color: 'text-green-600', classOnly: true },
  { to: '/admin/payments',              Icon: MdPayments,    label: 'Record Payment',  bg: 'bg-primary-light', color: 'text-primary'  },
  ...(TRIAL_ENABLED ? [{ to: '/admin/trial-students?source=public_website', Icon: MdLanguage, label: 'Website Requests', bg: 'bg-blue-50', color: 'text-blue-600' }] : []),
  { to: '/admin/students',              Icon: MdPeople,      label: 'Add Student',     bg: 'bg-blue-50',    color: 'text-blue-600'   },
  { to: '/admin/payments?tab=fees',     Icon: MdHourglassTop,label: 'Pending Fees',    bg: 'bg-red-50',     color: 'text-red-500'    },
  ...(APPROVALS_ENABLED ? [{ to: '/admin/approvals', Icon: MdToday, label: 'Approvals', bg: 'bg-purple-50', color: 'text-purple-600' }] : []),
];

function MobileQuickActions({ showClasses = true }) {
  const v = useClubVocab();
  const labelFor = (label) => label.replace(/\bStudents\b/g, v.members).replace(/\bStudent\b/g, v.member);
  return (
    <div className="md:hidden">
      <p className="text-[11px] font-medium text-subtle uppercase tracking-[0.12em] mb-2">Quick Actions</p>
      <div className="grid grid-cols-3 gap-2.5">
        {MOBILE_ACTIONS.filter(a => showClasses || !a.classOnly).map(({ to, Icon, label, bg, color }) => (
          <Link
            key={to}
            to={to}
            className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl p-3 ${bg} active:opacity-75 transition-opacity`}
            style={{ minHeight: 72 }}
          >
            <Icon className={`text-2xl ${color}`} />
            <span className={`text-[11px] font-semibold text-center leading-tight ${color}`}>{labelFor(label)}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Skeleton strip ─────────────────────────────────────────────────────────
function SkeletonStrip({ rows = 3 }) {
  return (
    <div className="space-y-2 p-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 bg-nav-hover animate-pulse rounded-xl" />
      ))}
    </div>
  );
}

// ── Main dashboard ─────────────────────────────────────────────────────────
function Dashboard() {
  document.title = 'Dashboard — TrainAxis';
  const { user }       = useAuth();
  const { formatDate } = useCalendar();
  const qc             = useQueryClient();
  const navigate       = useNavigate();
  const v              = useClubVocab();

  const [setupSkipped, setSetupSkipped] = useState(
    () => !!localStorage.getItem('ta_admin_setup_skipped')
  );

  const { data: cc, isPending } = useQuery({
    queryKey: ['command-center'],
    queryFn:  () => getCommandCenter().then(r => r.data),
    staleTime: 1000 * 60 * 2,
  });

  const { mutate: genFees, isPending: genLoading } = useMutation({
    // Identical to the Payments page "Generate Fees" button: POST /api/fees/generate { month }.
    mutationFn: () => generateMonthlyFees(currentMonth()),
    onSuccess:  (res) => {
      // Same created/skipped handling as the Payments page so both buttons behave alike.
      const created = res.data.created || 0;
      const skipped = res.data.skipped || 0;
      const total   = created + skipped;
      if (created === 0 && total > 0) {
        toast(`Fees for ${currentMonthLabel()} are already generated — all ${total} ${v.members.toLowerCase()} have records.`, { icon: 'ℹ️' });
      } else if (created > 0) {
        toast.success(`Generated fees for ${created} ${created !== 1 ? v.members.toLowerCase() : v.member.toLowerCase()} for ${currentMonthLabel()}.`);
      } else {
        toast(res.data.message || `No active ${v.members.toLowerCase()} to generate fees for.`);
      }
      qc.invalidateQueries({ queryKey: ['command-center'] }); // Dashboard's data source
      qc.invalidateQueries({ queryKey: ['fee-payments'] });   // keep Payments page fresh too
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to generate fees'),
  });


  const [confirm, confirmModal] = useConfirm();
  const handleGenerateFees = async () => {
    if (!(await confirm({
      title: 'Generate fee records?',
      message: `Generate fee records for ${currentMonthLabel()}?\n\nThis creates unpaid fee entries for all active ${v.members.toLowerCase()}.`,
      confirmLabel: 'Generate',
      destructive: false,
    }))) return;
    genFees();
  };

  const { club }      = useTenant();
  // Open-floor gyms hide the class/session/attendance widgets — the G3 membership cards +
  // finance snapshot become the dashboard core. Reversible, no data touched.
  const showClasses   = hasClasses(club?.clubType);

  const todaySessions = cc?.today?.sessions     || [];
  const ar            = cc?.actionsRequired      || {};
  const fin           = cc?.finance              || {};
  const stu           = cc?.students             || {};
  const belt          = cc?.beltProgress         || {};
  const sc            = cc?.setupChecklist;
  const wl            = cc?.websiteLeads         || {};
  const membership    = cc?.membership; // null for taekwondo → cards don't render

  // Pin checklist to top for brand-new clubs (fewer than 4 steps done)
  const checklistAtTop = sc && sc.completed < 4;

  // First in-progress belt exam (for direct link from action item)
  const firstGradingExam = belt.upcomingExams?.find(e => e.status === 'in_progress');

  // WhatsApp pre-filled message for overdue fees
  const totalFeesPending = (ar.overduePayments || 0) + (ar.unpaidPayments || 0);
  // NOT month-scoped — every overdue fee regardless of which month it was billed for. This
  // widget is pinned to the current month and can't be changed, so without this an old debt
  // (e.g. 60+ days overdue) would silently vanish the moment the current month has no fees
  // generated yet, even though totalFeesPending above would be 0 and hide the item entirely.
  const overdueAllTime       = ar.overdueAllTime || 0;
  const overdueAllTimeAmount = ar.overdueAllTimeAmount || 0;
  const waMsg = encodeURIComponent(
    `Hi, your monthly membership fee is overdue. Please make the payment at your earliest convenience. Thank you.`
  );

  // Build action items — only shown when count > 0
  const actionItems = [
    ar.pendingExpenses > 0 && {
      icon: MdReceipt,
      iconBg: 'bg-amber-50', iconColor: 'text-amber-600',
      title: `${ar.pendingExpenses} trainer expense${ar.pendingExpenses !== 1 ? 's' : ''} awaiting approval`,
      subtitle: 'Review receipts before approving',
      to: '/admin/expenses', linkLabel: 'Review',
    },
    (overdueAllTime > 0 || totalFeesPending > 0) && {
      icon: MdWarning,
      iconBg: 'bg-red-50', iconColor: 'text-red-500',
      // "outstanding" is reserved for total unpaid balance, due or not (the Collect/Receivables
      // meaning, ~39 students for this club) — this tile counts only the literal status==='overdue'
      // fees, a much narrower figure, so it must say "past due" instead. "overdue" on its own
      // always means a specific month's worth now — never bare, so it can't be misread as all-time.
      title: overdueAllTime > 0
        ? `${overdueAllTime} fee${overdueAllTime !== 1 ? 's' : ''} past due (all-time)`
        : `${totalFeesPending} fees not yet collected`,
      subtitle: overdueAllTime > 0
        ? `NPR ${overdueAllTimeAmount.toLocaleString()} past due · ${ar.unpaidPayments || 0} unpaid this month`
        : `${ar.overduePayments || 0} overdue this month · ${ar.unpaidPayments || 0} unpaid this month`,
      to: '/admin/payments', linkLabel: 'Collect',
      extra: (
        <a
          href={`https://wa.me/?text=${waMsg}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Send WhatsApp reminder"
          className="flex items-center gap-1 text-xs font-medium text-secondary bg-card border border-divider hover:bg-secondary hover:text-card hover:border-secondary px-2.5 py-1 rounded-full transition-all duration-200"
          onClick={e => e.stopPropagation()}
        >
          <FaWhatsapp className="text-sm" /> Remind
        </a>
      ),
    },
    APPROVALS_ENABLED && ar.trialApprovals > 0 && {
      icon: MdPeople,
      iconBg: 'bg-blue-50', iconColor: 'text-blue-600',
      title: `${ar.trialApprovals} new ${v.member.toLowerCase()} application${ar.trialApprovals !== 1 ? 's' : ''} pending`,
      subtitle: `${v.members} waiting for your approval`,
      to: '/admin/approvals', linkLabel: 'Review',
    },
    showClasses && ar.lowAttendanceStudents > 0 && {
      icon: MdTrendingDown,
      iconBg: 'bg-orange-50', iconColor: 'text-orange-500',
      title: `${ar.lowAttendanceStudents} ${v.member.toLowerCase()}${ar.lowAttendanceStudents !== 1 ? 's' : ''} with low attendance`,
      subtitle: '3+ absences in the last 14 days',
      to: '/admin/reports', linkLabel: 'View',
    },
    ar.inProgressExams > 0 && {
      icon: MdEmojiEvents,
      iconBg: 'bg-purple-50', iconColor: 'text-purple-600',
      title: `${ar.inProgressExams} belt exam${ar.inProgressExams !== 1 ? 's' : ''} needs grading`,
      subtitle: firstGradingExam ? `"${firstGradingExam.title}" — finalize results to promote ${v.members.toLowerCase()}` : `Finalize results to promote ${v.members.toLowerCase()}`,
      to: firstGradingExam ? `/admin/belt-exams?examId=${firstGradingExam._id}` : '/admin/belt-exams',
      linkLabel: 'Grade Now',
    },
    TRIAL_ENABLED && ar.trialPendingApprovals > 0 && {
      icon: MdLanguage,
      iconBg: 'bg-cyan-50', iconColor: 'text-cyan-600',
      title: `${ar.trialPendingApprovals} new website request${ar.trialPendingApprovals !== 1 ? 's' : ''}`,
      subtitle: 'New contacts from your public website — follow up',
      to: '/admin/trial-students?status=pending', linkLabel: 'Follow Up',
    },
    TRIAL_ENABLED && ar.trialReadyToConvert > 0 && {
      icon: MdPersonAdd,
      iconBg: 'bg-teal-50', iconColor: 'text-teal-600',
      title: `${ar.trialReadyToConvert} website request${ar.trialReadyToConvert !== 1 ? 's' : ''} ready to add`,
      subtitle: `Contacted visitors ready to join — add as ${v.member.toLowerCase()}`,
      to: '/admin/trial-students', linkLabel: `Add as ${v.member}`,
    },
  ].filter(Boolean);

  return (
    <div className="space-y-7">
      {confirmModal}

      {/* ── Welcome + Quick Actions ──────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-secondary">
            Good {greeting()}, {user?.name?.split(' ')[0] || 'Admin'}
          </h1>
          <p className="text-muted text-sm mt-1">Here is what needs your attention today.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {TRIAL_ENABLED && <QA to="/admin/trial-students?source=public_website" icon={MdLanguage} label="Website Requests" />}
          <QA to="/admin/students"       icon={MdPeople}      label={`Add ${v.member}`} />
          {showClasses && <QA to="/admin/attendance"     icon={MdSchedule}    label="Attendance"        />}
          <QA to="/admin/payments"       icon={MdPayments}    label="Record Payment"    primary />
          <QA to="/admin/expenses"       icon={MdReceipt}     label="Add Expense"       />
          <QA to="/admin/announcements"  icon={MdCampaign}    label="Announce"          />
          <QA
            onClick={handleGenerateFees}
            icon={MdAutorenew}
            label="Generate Fees"
            loading={genLoading}
          />
        </div>
      </div>

      {/* ── Mobile quick actions (only on small screens) ────────── */}
      <MobileQuickActions showClasses={showClasses} />

      {/* ── Start Setup card (brand-new clubs that haven't skipped) ── */}
      {sc && checklistAtTop && !setupSkipped && (
        <StartSetupCard onSkip={() => {
          localStorage.setItem('ta_admin_setup_skipped', '1');
          setSetupSkipped(true);
        }} />
      )}

      {/* ── Launch Readiness (top for brand-new clubs) ───────────── */}
      {sc && checklistAtTop && (
        <div id="setup-checklist">
          <SectionHead title="Getting Started" />
          <SetupChecklist sc={sc} />
        </div>
      )}

      {/* ── Action Required ──────────────────────────────────────── */}
      <div>
        <SectionHead title="Action Required" />
        <Card className="!p-0 overflow-hidden transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
          {isPending ? (
            <SkeletonStrip rows={2} />
          ) : actionItems.length === 0 ? (
            <div className="flex items-center gap-3 px-5 py-5">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <MdCheckCircle className="text-green-500 text-xl" />
              </div>
              <div>
                <p className="font-semibold text-secondary text-sm">All caught up!</p>
                <p className="text-xs text-muted mt-0.5">No pending tasks right now. Great work.</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-divider">
              {actionItems.map((item, i) => <ActionItem key={i} {...item} />)}
            </div>
          )}
        </Card>
      </div>

      {/* ── Today's Classes (hidden for open-floor gym clubs) ────── */}
      {showClasses && (
      <div>
        <SectionHead title="Today's Classes" action="All Classes" actionTo="/admin/attendance" />
        <Card className="!p-0 overflow-hidden transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
          {isPending ? (
            <SkeletonStrip rows={2} />
          ) : todaySessions.length === 0 ? (
            <div className="flex items-center gap-3 px-5 py-4">
              <div className="w-9 h-9 bg-nav-hover rounded-xl flex items-center justify-center flex-shrink-0">
                <MdToday className="text-muted text-lg" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-secondary">No classes scheduled today.</p>
                <Link to="/admin/classes" className="text-primary text-xs font-semibold hover:underline">
                  + Create a class
                </Link>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-divider">
              {todaySessions.map(s => <SessionRow key={s._id} session={s} />)}
            </div>
          )}
        </Card>
      </div>
      )}

      {/* ── Memberships (gym / yoga / general only) ──────────────── */}
      {membership && (
        <div>
          <SectionHead title="Memberships" action="Members" actionTo="/admin/students" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MembershipStatCard label="Active Members" value={membership.activeMembers} tone="green"
              icon={MdCheckCircle} onClick={() => navigate('/admin/students?membership=active')} />
            <MembershipStatCard label="Expiring This Week" value={membership.expiringSoon} tone="amber"
              icon={MdHourglassTop} onClick={() => navigate('/admin/students?membership=expiring')} />
            <MembershipStatCard label="Expired" value={membership.expired} tone="red"
              icon={MdWarning} onClick={() => navigate('/admin/students?membership=expired')} />
            <MembershipStatCard label="No Plan" value={membership.noPlan} tone="neutral"
              icon={MdCardMembership} onClick={() => navigate('/admin/students?membership=none')} />
          </div>
        </div>
      )}

      {/* ── Financial Snapshot ───────────────────────────────────── */}
      <div>
        <SectionHead title="This Month — Finance" action="Payments" actionTo="/admin/payments" />
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <FinCard
            label="Collected"
            value={`NPR ${fmt(fin.collectedThisMonth)}`}
            iconBg="bg-green-50"
            color="text-green-600"
            icon={MdTrendingUp}
          />
          <FinCard
            label="Pending Fees"
            value={totalFeesPending}
            sub={totalFeesPending > 0 ? `${ar.overduePayments || 0} overdue this month · ${ar.unpaidPayments || 0} unpaid` : 'All fees collected'}
            iconBg={totalFeesPending > 0 ? 'bg-red-50' : 'bg-green-50'}
            color={totalFeesPending > 0 ? 'text-red-500' : 'text-green-500'}
            icon={MdHourglassTop}
          />
          <FinCard
            label="Approved Expenses"
            value={`NPR ${fmt(fin.approvedExpensesThisMonth)}`}
            iconBg="bg-orange-50"
            color="text-orange-500"
            icon={MdReceipt}
          />
          <FinCard
            label="Net Balance"
            value={`NPR ${fmt(fin.netBalance)}`}
            sub="collected minus expenses"
            iconBg={(fin.netBalance || 0) >= 0 ? 'bg-blue-50' : 'bg-red-50'}
            color={(fin.netBalance || 0) >= 0 ? 'text-blue-600' : 'text-red-500'}
            icon={MdPayments}
          />
        </div>
      </div>

      {/* ── Student Health + Belt Progress ───────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Student Health */}
        <div>
          <SectionHead title={`Full ${v.members}`} action="View All" actionTo="/admin/students" />
          <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
            {/* Stat pills */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {[
                { label: 'Active',       value: stu.active        || 0, color: 'text-green-600', bg: 'bg-green-50'  },
                { label: 'Inactive',     value: stu.inactive      || 0, color: 'text-muted',  bg: 'bg-nav-hover' },
                // Attendance-based pills — hidden for open-floor gym clubs.
                ...(showClasses ? [{ label: 'Low Attend.',  value: stu.lowAttendance || 0, color: 'text-amber-600', bg: 'bg-amber-50'  }] : []),
                ...(showClasses ? [{
                  label: 'Club Rate',
                  value: stu.attendanceRate != null ? `${stu.attendanceRate}%` : '—',
                  color: stu.attendanceRate == null ? 'text-muted'
                       : stu.attendanceRate >= 75 ? 'text-green-600'
                       : stu.attendanceRate >= 50 ? 'text-amber-600'
                       : 'text-red-500',
                  bg: 'bg-blue-50',
                  icon: MdSpeed,
                }] : []),
              ].map(s => (
                <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
                  <p className={`text-xl font-bold leading-none ${s.color}`}>{s.value}</p>
                  <p className="text-[11px] text-muted mt-1 leading-tight">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Recent enrollments */}
            {isPending ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => <div key={i} className="h-8 bg-nav-hover animate-pulse rounded-lg" />)}
              </div>
            ) : (stu.recentEnrollments || []).length > 0 ? (
              <div>
                <p className="text-[11px] font-medium text-subtle uppercase tracking-[0.12em] mb-2">Recently Joined</p>
                <div className="space-y-2.5">
                  {stu.recentEnrollments.map(s => (
                    <div key={s._id} className="flex items-center gap-2.5">
                      <div className="w-7 h-7 bg-primary-light text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {(s.name || '?')[0].toUpperCase()}
                      </div>
                      <p className="flex-1 text-sm font-medium text-secondary truncate">{s.name}</p>
                      {s.currentGrade && (
                        <span className="text-[11px] bg-gray-100 text-muted px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                          {s.currentGrade}
                        </span>
                      )}
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.isActive ? 'bg-green-400' : 'bg-gray-300'}`} />
                    </div>
                  ))}
                </div>
              </div>
            ) : !isPending && (
              <p className="text-xs text-muted text-center py-3">
                No {v.members.toLowerCase()} yet.{' '}
                <Link to="/admin/students" className="text-primary font-semibold hover:underline">
                  Add a {v.member.toLowerCase()}
                </Link>
              </p>
            )}
          </Card>
        </div>

        {/* Belt Progress */}
        <div>
          <SectionHead title="Belt Exams" action="View All" actionTo="/admin/belt-exams" />
          <Card className="!p-0 overflow-hidden transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
            {isPending ? (
              <SkeletonStrip rows={2} />
            ) : (belt.upcomingExams || []).length === 0 ? (
              <div className="flex items-center gap-3 px-5 py-4">
                <div className="w-9 h-9 bg-nav-hover rounded-xl flex items-center justify-center flex-shrink-0">
                  <MdEmojiEvents className="text-muted text-lg" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-secondary">No upcoming belt exams.</p>
                  <Link to="/admin/belt-exams" className="text-primary text-xs font-semibold hover:underline">
                    + Schedule an exam
                  </Link>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-divider">
                {belt.upcomingExams.map(exam => (
                  <Link
                    key={exam._id}
                    to={`/admin/belt-exams?examId=${exam._id}`}
                    className="flex items-center gap-3 px-4 py-3.5 hover:bg-nav-hover transition-colors"
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      exam.status === 'in_progress' ? 'bg-purple-50' : 'bg-amber-50'
                    }`}>
                      <MdEmojiEvents className={`${
                        exam.status === 'in_progress' ? 'text-purple-600' : 'text-amber-600'
                      } text-lg`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-secondary text-sm leading-tight truncate">{exam.title}</p>
                      <p className="text-xs text-muted mt-0.5">
                        <DualDate date={exam.date} />{exam.participants > 0 ? ` · ${exam.participants} ${v.members.toLowerCase()}` : ''}
                      </p>
                    </div>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                      exam.status === 'in_progress'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {exam.status === 'in_progress' ? 'Grade Now' : 'Scheduled'}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* ── Recent Activity ──────────────────────────────────────── */}
      <div>
        <SectionHead title="Recent Activity" action="View All" actionTo="/admin/activity" />
        <RecentActivity />
      </div>

      {/* ── Launch Readiness (bottom for established clubs) ─────── */}
      {sc && !checklistAtTop && (
        <div>
          <SectionHead title="Getting Started" />
          <SetupChecklist sc={sc} />
        </div>
      )}

    </div>
  );
}

export default Dashboard;
